/**
 * NoteAuditDetail Component
 *
 * Detailed view of a single note audit
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate,useParams } from 'react-router-dom';
import { useGetRulesQuery } from '@features/rules/store/api';
import type { ComplianceRuleDocument } from '@features/rules/types';
import {
  ArrowBack as BackIcon,
  Description as NoteIcon,
  OpenInNew as OpenIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

import { TRIGGER_LABELS } from '../../constants';
import { useNoteAuditDetail } from '../../hooks/useNoteAuditDetail';
import type { AuditTrigger } from '../../types';
import { isValidNoteId } from '../../utils/validation';
import { RevisionHistory } from '../history/RevisionHistory';
import { JotAuditPageLayout } from '../layout';
import { DeltaBadge } from '../shared/DeltaBadge';
import { QualityScore } from '../shared/QualityScore';

import { CPTReviewPanel } from './CPTReviewPanel';
import { RuleComplianceTable } from './RuleComplianceTable';

/**
 * Format datetime to readable string
 */
const formatDateTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

export const NoteAuditDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { noteId, organizationId } = useParams<{ noteId: string; organizationId?: string }>();
  const { jotauditDashboard } = useEventTracking();
  const isAdminMode = Boolean(organizationId);
  const hasValidNoteId = isValidNoteId(noteId);

  const { audit, auditData, isLoading, error, refetch } = useNoteAuditDetail(
    hasValidNoteId ? noteId : undefined,
    {
      isAdminMode,
      organizationId,
    },
  );

  // Track note detail viewed when data loads
  const viewedTrackedRef = useRef<string | null>(null);
  const cptViewedTrackedRef = useRef<string | null>(null);
  
  useEffect(() => {
    if (audit && noteId && viewedTrackedRef.current !== noteId) {
      viewedTrackedRef.current = noteId;
      const revision = audit.current_revision;
      jotauditDashboard.noteDetailViewed({
        note_id: noteId,
        quality_score: revision?.overall_quality_score ?? undefined,
        compliance_percentage: revision?.compliance_percentage ?? undefined,
        cpt_match: revision?.cpt_match,
      });
    }
  }, [audit, noteId, jotauditDashboard]);

  // Track CPT review viewed when CPT data is available
  useEffect(() => {
    if (audit && noteId && cptViewedTrackedRef.current !== noteId) {
      const revision = audit.current_revision;
      if (revision && (revision.recommended_cpt_codes || revision.provider_cpt_codes)) {
        cptViewedTrackedRef.current = noteId;
        jotauditDashboard.cptReviewViewed({
          note_id: noteId,
          cpt_match: revision.cpt_match,
          provider_codes: revision.provider_cpt_codes?.join(', ') || '',
          recommended_codes: revision.recommended_cpt_codes?.join(', ') || '',
          has_billing_concerns: !revision.cpt_match,
        });
      }
    }
  }, [audit, noteId, jotauditDashboard]);

  // Fetch all compliance rules
  const { data: rulesData, isLoading: isRulesLoading } = useGetRulesQuery(
    {
      rule_type: 'compliance',
      per_page: 100, // API max is 100
      page: 1,
    },
    { skip: !auditData || auditData.rule_compliance_assessments.length === 0 },
  );

  // Create a map of rule_id -> rule data for quick lookup
  const rulesMap = useMemo(() => {
    if (!rulesData?.items) return new Map<string, ComplianceRuleDocument>();

    const map = new Map<string, ComplianceRuleDocument>();
    rulesData.items.forEach(item => {
      if (item.rule_type === 'compliance' && 'body_md' in item.rule) {
        map.set(item.rule_id, item.rule as ComplianceRuleDocument);
      }
    });
    return map;
  }, [rulesData]);

  const handleBack = () => {
    if (isAdminMode && organizationId) {
      navigate(`/admin-v2/jotaudit/${organizationId}?tab=notes`);
    } else {
      navigate('/jotaudit?tab=notes');
    }
  };

  const handleOpenNote = () => {
    if (noteId) {
      // Track open note from detail page
      jotauditDashboard.openNoteClicked({
        note_id: noteId,
        source: 'detail_page',
      });
      navigate(`/note/${noteId}`);
    }
  };

  const handleJumpToSection = (sectionId: string) => {
    // This would need integration with the note viewer to scroll to section
    console.log('Jump to section:', sectionId);
  };

  const handleRuleClick = (ruleId: string, complianceStatus: 'yes' | 'no' | 'warning') => {
    // Track rule compliance row clicked
    jotauditDashboard.ruleComplianceRowClicked({
      note_id: noteId,
      rule_id: ruleId,
      compliance_status: complianceStatus,
    });
  };

  if (!hasValidNoteId) {
    return (
      <JotAuditPageLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            <AlertTitle>Invalid note ID</AlertTitle>
            The note ID in the URL is missing or invalid.
          </Alert>
        </Box>
      </JotAuditPageLayout>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refetch}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Error loading audit details</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  const noteAudit = audit?.audit;
  const revision = audit?.current_revision;
  const trigger = revision?.trigger as AuditTrigger | undefined;
  const triggerLabel = trigger ? TRIGGER_LABELS[trigger] || trigger : '—';

  return (
    <JotAuditPageLayout>
      {/* Page Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ mr: 0.5 }}>
            <BackIcon />
          </IconButton>

          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Skeleton variant="rounded" width={48} height={48} />
              <Box>
                <Skeleton width={200} height={28} />
                <Skeleton width={120} height={20} />
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  bgcolor: theme.palette.primary.light + '20',
                  color: theme.palette.primary.main,
                }}
              >
                <NoteIcon />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Note Audit
                </Typography>
                {noteAudit?.note_title && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                      mt: 0.5,
                    }}
                  >
                    {noteAudit.note_title}
                  </Typography>
                )}
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 0.5 }}
                >
                  {audit?.patient_name && (
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Patient: <strong>{audit.patient_name}</strong>
                    </Typography>
                  )}
                  {audit?.provider_name && (
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Provider: <strong>{audit.provider_name}</strong>
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {noteAudit ? formatDateTime(noteAudit.last_audit_at) : '—'} • {triggerLabel}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<OpenIcon />}
                onClick={handleOpenNote}
                sx={{ textTransform: 'none' }}
              >
                View Note
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* CPT Mismatch Warning */}
      {revision?.cpt_match === false && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <AlertTitle>CPT Code Mismatch</AlertTitle>
          Provider code
          {revision.provider_cpt_codes && revision.provider_cpt_codes.length > 1 ? 's' : ''} (
          {revision.provider_cpt_codes && revision.provider_cpt_codes.length > 0
            ? revision.provider_cpt_codes.join(', ')
            : '—'}
          ){' '}
          {revision.provider_cpt_codes && revision.provider_cpt_codes.length > 1
            ? 'differ'
            : 'differs'}{' '}
          from AI recommendation
          {(revision?.recommended_cpt_codes?.length ?? 0) > 1 ? 's' : ''} (
          {revision?.recommended_cpt_codes && revision.recommended_cpt_codes.length > 0
            ? revision.recommended_cpt_codes.join(', ')
            : '—'}
          ). Review documentation to ensure proper billing.
        </Alert>
      )}

      {/* Main Content */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Quality Score */}
          <Grid item xs={6} sm={4}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                bgcolor: theme.palette.grey[50],
              }}
            >
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Quality Score
              </Typography>
              {isLoading ? (
                <Skeleton width={60} height={32} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <QualityScore
                    score={revision?.overall_quality_score}
                    variant="badge"
                    size="medium"
                  />
                  {revision?.quality_delta !== null && revision?.quality_delta !== undefined && (
                    <DeltaBadge delta={revision.quality_delta} size="small" />
                  )}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Compliance */}
          <Grid item xs={6} sm={4}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                bgcolor: theme.palette.grey[50],
              }}
            >
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Compliance
              </Typography>
              {isLoading ? (
                <Skeleton width={60} height={32} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {revision?.compliance_percentage !== null &&
                    revision?.compliance_percentage !== undefined
                      ? `${revision.compliance_percentage.toFixed(0)}%`
                      : '—'}
                  </Typography>
                  {revision?.compliance_delta !== null &&
                    revision?.compliance_delta !== undefined && (
                      <DeltaBadge delta={revision.compliance_delta} label="%" size="small" />
                    )}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Revision */}
          <Grid item xs={6} sm={4}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                bgcolor: theme.palette.grey[50],
              }}
            >
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Revision
              </Typography>
              {isLoading ? (
                <Skeleton width={60} height={32} />
              ) : (
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {revision?.revision || 1} of {noteAudit?.total_revisions || 1}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Billing Review & Revision History Row */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Billing Review - Left side on larger screens */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Billing Review
            </Typography>
            {isLoading ? (
              <Skeleton variant="rounded" height={200} />
            ) : (
              <CPTReviewPanel
                recommendedCodes={revision?.recommended_cpt_codes || null}
                providerCodes={revision?.provider_cpt_codes || null}
                cptMatch={revision?.cpt_match}
                billingSupport={auditData?.billing_support || null}
              />
            )}
          </Grid>

          {/* Revision History - Right side on larger screens */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Revision History
            </Typography>
            <RevisionHistory noteId={noteId} organizationId={organizationId} />
          </Grid>
        </Grid>

        {/* Enrichment Rationale - Admin only */}
        {organizationId && (auditData as any)?.enrichment_rationale && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Enrichment Rationale
            </Typography>

            {/* Summary */}
            {(auditData as any).enrichment_rationale.summary && (
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.5,
                  mb: 2,
                  fontSize: '0.875rem',
                }}
              >
                {(auditData as any).enrichment_rationale.summary}
              </Typography>
            )}

            {/* Per-code rationale */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {(auditData as any).enrichment_rationale.line_item_rationale?.map((item: any, idx: number) => (
                <Paper
                  key={idx}
                  elevation={0}
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    borderRadius: 2,
                  }}
                >
                  {/* Code header + confidence */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Chip
                      label={item.code_id}
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        bgcolor: `${theme.palette.primary.main}15`,
                        color: theme.palette.primary.main,
                        border: `1px solid ${theme.palette.primary.main}40`,
                        height: 26,
                        '& .MuiChip-label': { px: 1.25 },
                      }}
                    />
                    {item.confidence != null && (
                      <Chip
                        label={`${Math.round(item.confidence * 100)}% confidence`}
                        size="small"
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          height: 22,
                          bgcolor:
                            item.confidence >= 0.8
                              ? `${theme.palette.success.main}12`
                              : item.confidence >= 0.5
                                ? `${theme.palette.warning.main}12`
                                : `${theme.palette.error.main}12`,
                          color:
                            item.confidence >= 0.8
                              ? theme.palette.success.dark
                              : item.confidence >= 0.5
                                ? theme.palette.warning.dark
                                : theme.palette.error.dark,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    )}
                  </Box>

                  {/* Rationale text */}
                  {item.rationale && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        mb: item.evidence?.length > 0 ? 1.5 : 0,
                      }}
                    >
                      {item.rationale}
                    </Typography>
                  )}

                  {/* Evidence list */}
                  {item.evidence?.length > 0 && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.secondary,
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          display: 'block',
                          mb: 0.75,
                        }}
                      >
                        Supporting Evidence
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {item.evidence.map((e: string, eIdx: number) => (
                          <Box
                            component="li"
                            key={eIdx}
                            sx={{
                              mb: 0.5,
                              '&::marker': { color: theme.palette.grey[400] },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                lineHeight: 1.5,
                                fontSize: '0.8rem',
                              }}
                            >
                              {e}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* Rule Compliance Section - Full width with scroll */}
        {!isLoading && auditData && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Rule Compliance
            </Typography>
            <Box
              sx={{
                maxHeight: '80vh',
                overflowY: 'auto',
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
              }}
            >
              <RuleComplianceTable
                assessments={auditData.rule_compliance_assessments}
                rulesMap={rulesMap}
                isLoadingRules={isRulesLoading}
                onJumpToSection={handleJumpToSection}
                onRuleClick={handleRuleClick}
              />
            </Box>
          </Box>
        )}
      </Paper>
    </JotAuditPageLayout>
  );
};

export default NoteAuditDetail;
